import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps, LazyImageStyle } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import PaginationImageGallery from "@store/Components/Store/ImageGallery/PaginationImageGallery";
import SlideshowImageGallery from "@store/Components/Store/ImageGallery/SlideshowImageGallery";
import getImageGalleryList from "@store/Store/Data/Gallery/Handlers/GetImageGallery";
import * as React from "react";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

export interface ImageGalleryStoreStyles {
    wrapperGallery?: GridContainerProps;
    imageTileGridItem?: GridItemProps;
    galleryPhotoLink?: LinkPresentationProps;
    imageGallerySingleImg?: LazyImageProps;
    galleryPhotoModal?: ModalPresentationProps;
}

export interface GalleryPhotosModel {
    altText: string;
    id: string;
    imageType: string;
    largeImagePath: string;
    mediumImagePath: string;
    name: string;
    smallImagePath: string;
    sortOrder: number;
}

export interface GalleryPaginationModel {
    page: number;
    pageSize: number;
}

export const imageGalleryStyles: ImageGalleryStoreStyles = {
    wrapperGallery: {
        css: css`
            width: 100%;
            display: flex;
            flex-wrap: wrap;
            gap: 20px 2%;
        `,
    },
    imageTileGridItem: {
        width: [6, 6, 4, 3, 3],
        css: css`
            &::before {
                content: "";
                padding-top: 60%;
            }
        `,
    },
    galleryPhotoLink: {
        css: css`
            width: 100%;
            height: 100%;
        `,
        typographyProps: {
            css: css`
                width: 100%;
                height: 100%;
                overflow: hidden;
                position: relative;

                ${LazyImageStyle} {
                    width: 100%;
                    height: 100%;

                    img {
                        width: auto;
                        height: 130%;
                        display: block;
                        position: absolute;
                        top: -15%;
                        left: 50%;
                        transform: translateX(-50%);
                    }
                }
            `,
        },
    },
    galleryPhotoModal: {
        cssOverrides: {
            modalBody: css`
                height: auto !important;
            `,
        },
    },
};

const styles = imageGalleryStyles;

const ImageGalleryStore = () => {
    const dispatch = useDispatch();
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [imageGalleryList, setImageGalleryList] = useState<any>({});
    const [galleryPhotos, setGalleryPhotos] = useState<Array<GalleryPhotosModel>>();
    const [galleryPagination, setGalleryPagination] = useState<any>();
    const [parameters, setParameters] = useState<GalleryPaginationModel>({ page: 1, pageSize: 8 });

    if (!imageGalleryList) {
        return null;
    }

    const photoSlideshowHandler = (index: number) => {
        setSelectedIndex(index);
        setModalIsOpen(true);
    };

    const modalCloseHandler = () => {
        setModalIsOpen(false);
        setSelectedIndex(0);
    };

    useEffect(() => {
        dispatch(
            getImageGalleryList({
                page: parameters.page,
                pageSize: parameters.pageSize,
                onComplete(resultProps) {
                    setImageGalleryList(resultProps?.apiResult?.result);
                },
            }),
        );
    }, [parameters]);

    useEffect(() => {
        const sortedGallery = imageGalleryList?.galleryPhotos as GalleryPhotosModel[];

        if (sortedGallery !== undefined) {
            sortedGallery.sort((a, b) => {
                return b.sortOrder - a.sortOrder;
            });
        }

        setGalleryPhotos(sortedGallery);
        setGalleryPagination(imageGalleryList?.pagination);
    }, [imageGalleryList]);

    if (!galleryPhotos || !galleryPagination) {
        return null;
    }

    return (
        <>
            <PaginationImageGallery
                pagination={galleryPagination}
                setParameter={setParameters}
                parameter={parameters}
            />
            <GridContainer {...styles.wrapperGallery}>
                {galleryPhotos.map(({ mediumImagePath, altText, id }, index) => {
                    return (
                        <GridItem {...styles.imageTileGridItem} key={`gallery_${id}`}>
                            <Link {...styles.galleryPhotoLink} onClick={() => photoSlideshowHandler(index)}>
                                <LazyImage {...styles.imageGallerySingleImg} src={mediumImagePath} altText={altText} />
                            </Link>
                        </GridItem>
                    );
                })}
                <Modal
                    headline={galleryPhotos[selectedIndex]?.name ?? translate("Photo Gallery")}
                    {...styles.galleryPhotoModal}
                    isOpen={modalIsOpen}
                    handleClose={modalCloseHandler}
                >
                    <SlideshowImageGallery
                        rawDataCollection={imageGalleryList}
                        imageCollection={galleryPhotos}
                        setSlideIndex={setSelectedIndex}
                        slideIndex={selectedIndex}
                    />
                </Modal>
            </GridContainer>
            {parameters.pageSize > 10 && (
                <PaginationImageGallery
                    pagination={galleryPagination}
                    setParameter={setParameters}
                    parameter={parameters}
                />
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: ImageGalleryStore,
    definition: {
        displayName: "Image Gallery",
        group: "Common",
        icon: "Image",
    },
};

export default widgetModule;
